import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Add, InfoCircle, Notification, Refresh, Repeat, Trash, VolumeHigh } from 'iconsax-reactjs';
import { alertDirectionFor, type Candle, type PriceAlert } from '@pipyar/core';
import { api } from '@/lib/api';
import { useTheme } from '@/lib/useTheme';
import { useSymbol } from '@/lib/useSymbol';
import { useI18n } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { useAlertSound } from '@/lib/useAlertSound';
import { notifyPermission, requestNotifyPermission, showNotification, type NotifyPermission } from '@/lib/notify';
import { Button } from '@/components/ui/Button';
import { AlertChart } from '@/components/chart/AlertChart';
import { ChartFrame } from '@/components/chart/ChartFrame';
import { useAlerts, useCreateAlert, useDeleteAlert, useQuote, useUpdateAlert } from './api';

interface CandlesResponse {
  symbol: string;
  digits: number;
  candles: Candle[];
}

/** The chart this module is built around. */
const TIMEFRAME = 'M5';

/** A price older than this is shown as stale — the feed has stopped. */
const STALE_AFTER_SEC = 120;

/**
 * Price alerts: an M5 chart where each alert is a line you drag, MT5 style.
 *
 * Double-click the chart (or use "New line") to place one; drag it to move it.
 * Which side it fires on follows from where it sits relative to price when it
 * is dropped, so there is no above/below setting to get wrong.
 */
export function AlertsPage() {
  const { theme } = useTheme();
  const { symbol } = useSymbol();
  const { t } = useI18n();
  const [placing, setPlacing] = useState(false);
  const [permission, setPermission] = useState<NotifyPermission>(notifyPermission);
  const { play } = useAlertSound('price');

  const candlesQuery = useQuery({
    queryKey: ['alert-candles', symbol],
    queryFn: () =>
      api.get<CandlesResponse>(
        `/api/market/candles?symbol=${encodeURIComponent(symbol!)}&timeframe=${TIMEFRAME}&limit=600`,
      ),
    enabled: Boolean(symbol),
    refetchInterval: 10_000,
    staleTime: 5_000,
  });
  const quoteQuery = useQuote(symbol);
  const alertsQuery = useAlerts();
  const create = useCreateAlert();
  const update = useUpdateAlert();
  const remove = useDeleteAlert();

  const quote = quoteQuery.data;
  const digits = quote?.digits ?? candlesQuery.data?.digits ?? 2;
  const alerts = useMemo(
    () => (alertsQuery.data ?? []).filter((a) => a.symbol === symbol),
    [alertsQuery.data, symbol],
  );

  // The chart's last candle follows the live price by itself (useLiveBar);
  // the query only needs to bring in closed bars.
  const candles = useMemo(() => candlesQuery.data?.candles ?? [], [candlesQuery.data]);

  const nowSec = Date.now() / 1000;
  const stale = quote?.updatedAt != null && nowSec - quote.updatedAt > STALE_AFTER_SEC;

  const place = (price: number) => {
    setPlacing(false);
    if (!symbol || !quote) return;
    create.mutate({ symbol, price, direction: alertDirectionFor(price, quote.price), note: '' });
  };

  const move = (id: number, price: number) => {
    if (!quote) return;
    update.mutate({ id, patch: { price, direction: alertDirectionFor(price, quote.price) } });
  };

  const enableNotifications = async () => {
    const next = await requestNotifyPermission();
    setPermission(next);
    if (next === 'granted') {
      void showNotification(t('alerts.title'), t('alerts.notifyTestBody'), 'price-alert-test');
    }
  };

  const error = candlesQuery.error ?? quoteQuery.error ?? alertsQuery.error;

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('alerts.title')}</h1>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {t('alerts.subtitle', { symbol: symbol ?? '—' })}
          </p>
        </div>

        {quote && (
          <div className="ms-auto flex items-baseline gap-2">
            <span className="tabular text-2xl font-semibold tracking-tight">
              {quote.price.toFixed(digits)}
            </span>
            <span
              className={cn(
                'rounded px-1.5 py-0.5 text-[10px] font-medium',
                stale ? 'bg-danger/12 text-danger' : 'bg-bull/12 text-bull',
              )}
            >
              {stale ? t('alerts.stale') : t('alerts.live')}
            </span>
          </div>
        )}
        <Button
          variant="ghost"
          size="icon"
          aria-label={t('common.refresh')}
          className={quote ? undefined : 'ms-auto'}
          onClick={() => {
            void candlesQuery.refetch();
            void quoteQuery.refetch();
            void alertsQuery.refetch();
          }}
        >
          <Refresh size={18} className={candlesQuery.isFetching ? 'animate-spin' : undefined} />
        </Button>
      </header>

      {error && (
        <div
          role="alert"
          className="flex gap-2.5 rounded-lg border border-danger/30 bg-danger/8 p-3 text-sm text-danger"
        >
          <InfoCircle size={18} variant="Bold" className="mt-px shrink-0" />
          <span>{error instanceof Error ? error.message : t('alerts.error')}</span>
        </div>
      )}

      {stale && (
        <div className="flex gap-2.5 rounded-lg border border-border bg-surface/40 p-3 text-xs text-muted-foreground">
          <InfoCircle size={16} className="mt-px shrink-0" />
          <span>{t('alerts.staleNote')}</span>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
        {/* min-w-0: see KnotsPage — a 1fr column otherwise cannot shrink back after full screen. */}
        <section className="order-2 min-w-0 space-y-3 lg:order-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-lg bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground">
              {TIMEFRAME}
            </span>
            <Button
              variant={placing ? 'primary' : 'secondary'}
              size="sm"
              disabled={!quote}
              onClick={() => setPlacing((p) => !p)}
            >
              <Add size={16} />
              {placing ? t('alerts.clickChart') : t('alerts.newLine')}
            </Button>
            <span className="ms-auto text-xs text-muted-foreground">{t('alerts.hint')}</span>
          </div>

          <ChartFrame
            label={t('alerts.chart')}
            className="group/frame h-[32rem] overflow-hidden glass rounded-2xl"
          >
            {candlesQuery.isPending ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                {t('common.loading')}
              </div>
            ) : candles.length === 0 ? (
              <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                {t('alerts.noCandles')}
              </div>
            ) : (
              <AlertChart
                className="h-full w-full"
                candles={candles}
                alerts={alerts}
                digits={digits}
                theme={theme}
                resetKey={symbol}
                placing={placing}
                onCreate={place}
                onMove={move}
              />
            )}
          </ChartFrame>
        </section>

        <aside className="order-1 space-y-3 lg:order-2">
          <div className="glass rounded-2xl p-3">
            <h2 className="mb-2 text-sm font-medium">{t('alerts.delivery')}</h2>
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" onClick={() => play()}>
                <VolumeHigh size={16} />
                {t('alerts.testSound')}
              </Button>
              {permission === 'granted' ? (
                <span className="flex items-center gap-1.5 text-xs text-bull">
                  <Notification size={15} variant="Bold" />
                  {t('alerts.notifyOn')}
                </span>
              ) : permission === 'unsupported' ? (
                <span className="text-xs text-muted-foreground">{t('alerts.notifyUnsupported')}</span>
              ) : (
                <Button variant="secondary" size="sm" onClick={() => void enableNotifications()}>
                  <Notification size={16} />
                  {permission === 'denied' ? t('alerts.notifyBlocked') : t('alerts.notifyEnable')}
                </Button>
              )}
            </div>
            <p className="mt-2 text-[10px] leading-snug text-muted-foreground">{t('alerts.deliveryNote')}</p>
          </div>

          <div className="glass rounded-2xl p-3">
            <h2 className="mb-2 text-sm font-medium">{t('alerts.lines')}</h2>
            {alerts.length === 0 ? (
              <p className="text-xs text-muted-foreground">{t('alerts.none')}</p>
            ) : (
              <ul className="space-y-2">
                {alerts.map((a) => (
                  <AlertRow
                    key={a.id}
                    alert={a}
                    digits={digits}
                    reference={quote?.price}
                    onPrice={(price) => move(a.id, price)}
                    onNote={(note) => update.mutate({ id: a.id, patch: { note } })}
                    onToggle={() => update.mutate({ id: a.id, patch: { enabled: !a.enabled } })}
                    onRearm={() =>
                      update.mutate({
                        id: a.id,
                        patch: {
                          rearm: true,
                          ...(quote ? { direction: alertDirectionFor(a.price, quote.price) } : {}),
                        },
                      })
                    }
                    onDelete={() => remove.mutate(a.id)}
                  />
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function AlertRow({
  alert,
  digits,
  reference,
  onPrice,
  onNote,
  onToggle,
  onRearm,
  onDelete,
}: {
  alert: PriceAlert;
  digits: number;
  reference: number | undefined;
  onPrice: (price: number) => void;
  onNote: (note: string) => void;
  onToggle: () => void;
  onRearm: () => void;
  onDelete: () => void;
}) {
  const { t } = useI18n();
  const fired = alert.triggeredAt !== null;
  const distance = reference === undefined ? null : alert.price - reference;

  return (
    <li className={cn('rounded-xl bg-elevated/60 p-2.5', !alert.enabled && 'opacity-60')}>
      <div className="flex items-center gap-2">
        <span
          className={cn(
            'text-xs font-semibold',
            alert.direction === 'above' ? 'text-bull' : 'text-bear',
          )}
          title={t(alert.direction === 'above' ? 'alerts.firesAbove' : 'alerts.firesBelow')}
        >
          {alert.direction === 'above' ? '▲' : '▼'}
        </span>
        <input
          key={alert.price}
          type="number"
          step={10 ** -digits}
          defaultValue={alert.price.toFixed(digits)}
          onBlur={(e) => {
            const v = Number(e.target.value);
            if (v > 0 && v !== alert.price) onPrice(Number(v.toFixed(digits)));
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          aria-label={t('alerts.price')}
          className="tabular w-24 rounded-md border border-border bg-background px-1.5 py-0.5 text-sm"
        />
        {distance !== null && (
          <span className="tabular text-[11px] text-muted-foreground">
            {distance >= 0 ? '+' : ''}
            {distance.toFixed(digits)}
          </span>
        )}
        <span className="ms-auto flex items-center gap-0.5">
          {fired && (
            <button
              type="button"
              onClick={onRearm}
              title={t('alerts.rearm')}
              aria-label={t('alerts.rearm')}
              className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <Repeat size={15} />
            </button>
          )}
          <button
            type="button"
            onClick={onDelete}
            title={t('alerts.delete')}
            aria-label={t('alerts.delete')}
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-danger"
          >
            <Trash size={15} />
          </button>
        </span>
      </div>

      <div className="mt-1.5 flex items-center gap-2">
        <input
          key={alert.note}
          type="text"
          maxLength={128}
          defaultValue={alert.note}
          placeholder={t('alerts.notePlaceholder')}
          onBlur={(e) => {
            if (e.target.value !== alert.note) onNote(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          }}
          className="min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-1 py-0.5 text-xs text-muted-foreground hover:border-border focus:border-border"
        />
        <label className="flex cursor-pointer items-center gap-1 text-[11px] text-muted-foreground select-none">
          <input
            type="checkbox"
            checked={alert.enabled}
            onChange={onToggle}
            className="size-3.5 accent-[var(--primary)]"
          />
          {t('alerts.on')}
        </label>
      </div>

      <p className="mt-1 text-[10px] text-muted-foreground">
        {fired
          ? t('alerts.firedAt', {
              price: alert.triggeredPrice?.toFixed(digits) ?? '—',
              time: new Date(alert.triggeredAt! * 1000).toLocaleString(),
            })
          : t(alert.direction === 'above' ? 'alerts.firesAbove' : 'alerts.firesBelow')}
      </p>
    </li>
  );
}
